import type { TaskListItemDto, TeamMemberDto } from '@ovo/shared'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { errorMessage } from '../api/client.ts'
import { useMe, usePeopleFlags, useSetPeopleFlags, useTasks, useTeam, useTeamShifts } from '../api/hooks.ts'
import { formatTime } from '../lib/dates.ts'
import { useNow } from '../lib/hooks.ts'
import { PERSON_FORMS, pl, plural, TASK_FORMS } from '../lib/plural.ts'
import { useMainButton } from '../telegram/buttons.tsx'
import { Avatar } from '../ui/Avatar.tsx'
import { Chip } from '../ui/Chip.tsx'
import { EmptyState, ErrorState } from '../ui/EmptyState.tsx'
import { SwitchRow } from '../ui/Input.tsx'
import { Cell, Section } from '../ui/List.tsx'
import { Screen, ScreenHeader } from '../ui/ScreenHeader.tsx'
import { SkeletonList } from '../ui/Skeleton.tsx'
import { snackbar } from '../ui/Snackbar.tsx'
import { useTaskActions } from '../ui/TaskActions.tsx'
import { TaskCell } from '../ui/TaskCell.tsx'

function OnTime({ rate }: { rate: number | null }) {
  if (rate === null) return <span className="hint">мало данных</span>
  const pct = Math.round(rate * 100)
  return (
    <span className="row row--tight nowrap" title="Доля закрытых в срок за 30 дней">
      <span className="bar" aria-hidden="true">
        <span className="bar__fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="num">{pct}% в срок</span>
    </span>
  )
}

const MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' }

/** Где сотрудник сейчас: на работе, отошёл или день не начат (если он отмечает рабочий день). */
function PresenceLine({ userId }: { userId: number }) {
  const shifts = useTeamShifts()
  const s = shifts.data?.find((x) => x.user.id === userId)
  if (!s) return null
  const text = s.away
    ? `🟠 Отошёл до ${formatTime(new Date(s.away.until))}`
    : s.openSince
      ? `🟢 На работе с ${formatTime(new Date(s.openSince))}`
      : '⚪ Рабочий день не начат'
  return <div className="hint hint--md">{text}</div>
}

function memberSubtitle(m: TeamMemberDto): string {
  const parts = [`${m.open} открыто`, ...(m.closed30d ? [`закрыто за 30 дн.: ${m.closed30d}`] : [])]
  if (m.overdue) parts.push(`${m.overdue} со сдвинутым сроком`)
  if (m.awaitingAcceptance) {
    parts.push(`${m.awaitingAcceptance} ${plural(m.awaitingAcceptance, ['ждёт', 'ждут', 'ждут'])} принятия`)
  }
  if (m.submittedToReview) parts.push(`${m.submittedToReview} на проверке`)
  return parts.join(' · ')
}

export function TeamScreen() {
  const me = useMe()
  const isManager = Boolean(me.data?.isManager)
  const [branch, setBranch] = useState(false)
  // сервер отдаёт прямых подчинённых, ?branch=1 — всю ветку
  const team = useTeam(branch, isManager)

  const list = useMemo(
    () =>
      (team.data ?? [])
        .filter((m) => branch || m.direct)
        // По рейтингу за 30 дней; без места (мало данных) — по алфавиту в конце
        .sort(
          (a, b) =>
            (a.rank30d ?? 9999) - (b.rank30d ?? 9999) ||
            a.user.displayName.localeCompare(b.user.displayName, 'ru'),
        ),
    [team.data, branch],
  )

  let body: ReactNode
  if (me.isPending || (isManager && team.isPending)) body = <SkeletonList rows={4} />
  else if (!isManager) {
    body = (
      <EmptyState
        title="Раздел для руководителей"
        text="Здесь видно нагрузку команды, если у вас есть подчинённые."
      />
    )
  } else if (team.isError)
    body = <ErrorState text={errorMessage(team.error)} onRetry={() => void team.refetch()} />
  else if (list.length === 0)
    body = <EmptyState title="В команде пока никого" text="Сотрудников добавляет администратор." />
  else {
    body = (
      <Section
        id="team"
        title={branch ? 'Вся ветка' : 'Прямые подчинённые'}
        count={list.length}
        footer="Рейтинг за 30 дней: % закрытых в срок, затем сколько закрыто. Видите только вы — сотрудникам он не показывается."
      >
        {list.map((m) => (
          <Cell
            key={m.user.id}
            to={`/team/${m.user.id}`}
            before={<Avatar name={m.user.displayName} id={m.user.id} size={40} photo={m.photoVersion} />}
            title={`${m.rank30d ? `${MEDALS[m.rank30d] ?? `№${m.rank30d}`} ` : ''}${m.user.displayName}`}
            subtitle={
              <>
                {memberSubtitle(m)}
                <br />
                <OnTime rate={m.onTimeRate30d} />
              </>
            }
            chevron
          />
        ))}
      </Section>
    )
  }

  return (
    <Screen root label="Команда">
      <ScreenHeader title="Команда" />
      {isManager && team.data && (
        <p className="screen-subtitle">
          {pl(team.data.filter((m) => branch || m.direct).length, PERSON_FORMS)}
        </p>
      )}
      {isManager && (
        <div className="chips">
          <Chip selected={!branch} onClick={() => setBranch(false)}>
            Прямые
          </Chip>
          <Chip selected={branch} onClick={() => setBranch(true)}>
            Вся ветка
          </Chip>
        </div>
      )}
      {body}
    </Screen>
  )
}

/** Смены и стендап сотрудника — включает руководитель. */
function MemberFlags({ userId }: { userId: number }) {
  const flags = usePeopleFlags(userId > 0 ? userId : null)
  const set = useSetPeopleFlags(userId)
  if (!flags.data) return null
  const change = (patch: { shiftsEnabled?: boolean; standupEnabled?: boolean }) =>
    set.mutate(patch, {
      onSuccess: () => snackbar.success('Готово — сотрудник получит сообщение в боте'),
      onError: (e) => snackbar.show({ text: errorMessage(e) }),
    })
  return (
    <Section id="m-flags" title="Процессы">
      <SwitchRow
        label="Отметка рабочего дня"
        hint="Кнопки «Начать день», «Отойти», «Завершить день» в боте — учёт часов и переработок. По умолчанию включено у всех"
        checked={flags.data.shiftsEnabled}
        disabled={set.isPending}
        onChange={(v) => change({ shiftsEnabled: v })}
      />
      <SwitchRow
        label="Утренний стендап"
        hint="В начале дня бот спросит о планах, вам — сводка в 11:00"
        checked={flags.data.standupEnabled}
        disabled={set.isPending}
        onChange={(v) => change({ standupEnabled: v })}
      />
    </Section>
  )
}

export function TeamMemberScreen() {
  const { userId: raw } = useParams()
  const userId = Number(raw)
  const navigate = useNavigate()
  const me = useMe()
  const meId = me.data?.id ?? 0
  const team = useTeam(true, Boolean(me.data?.isManager))
  const member = team.data?.find((m) => m.user.id === userId)
  const tasks = useTasks({ scope: 'team', userId }, Boolean(me.data) && userId > 0)
  const now = useNow()
  const actions = useTaskActions(meId)

  useMainButton({
    text: 'Поставить задачу',
    onClick: () => navigate(`/tasks/new?assignee=${userId}`),
  })

  const list = tasks.data ?? []
  const overdue = list.filter((t) => t.isOverdue)
  const rest = list.filter((t) => !t.isOverdue)
  const cell = (t: TaskListItemDto) => (
    <TaskCell key={t.id} task={t} meId={meId} now={now} onGlyph={(task) => actions.openMenu(task)} />
  )

  return (
    <Screen label="Задачи сотрудника">
      <ScreenHeader back title={member?.user.displayName ?? 'Сотрудник'} small />
      {member && (
        <div className="profile">
          <Avatar name={member.user.displayName} id={member.user.id} size={72} photo={member.photoVersion} />
          <div className="grow stack stack--xs">
            <div className="hint hint--md">
              {[member.user.position, member.user.departmentName].filter(Boolean).join(' · ')}
            </div>
            <PresenceLine userId={member.user.id} />
            <div className="hint">{memberSubtitle(member)}</div>
            <OnTime rate={member.onTimeRate30d} />
          </div>
        </div>
      )}
      {member && member.oneOnOneCount > 0 && (
        <div className="banner">
          <div className="banner__body">
            <div className="banner__title">Обсудить на 1:1</div>
            <div className="hint hint--md">
              {pl(member.oneOnOneCount, TASK_FORMS)} долго без движения — возможно, нужна помощь или новый
              срок.
            </div>
          </div>
        </div>
      )}
      <MemberFlags userId={userId} />
      {tasks.isPending ? (
        <SkeletonList rows={4} />
      ) : tasks.isError ? (
        <ErrorState text={errorMessage(tasks.error)} onRetry={() => void tasks.refetch()} />
      ) : list.length === 0 ? (
        <EmptyState title="Открытых задач нет" text="Можно поставить новую — кнопка внизу." />
      ) : (
        <>
          {overdue.length > 0 && (
            <Section id="m-overdue" title="Срок прошёл" count={overdue.length} tone="attn">
              {overdue.map(cell)}
            </Section>
          )}
          {rest.length > 0 && (
            <Section id="m-open" title="В работе" count={rest.length}>
              {rest.map(cell)}
            </Section>
          )}
        </>
      )}
      {actions.element}
    </Screen>
  )
}
